import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import travelPolicyAssistantService from '../../../services/travelPolicyAssistantService';

const TravelPolicyAssistant = ({ travelRequest = null }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your AI travel policy assistant. I can help you with:\n\n• Travel policy questions and clarifications\n• Risk assessments for travel requests\n• Compliance requirements\n• Budget guidelines\n• Emergency travel procedures\n\nHow can I assist you today?',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [quickAction, setQuickAction] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage?.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages?.map((msg) => ({
        role: msg?.role,
        content: msg?.content,
      }));

      const response = await travelPolicyAssistantService?.getChatCompletion(
        inputMessage,
        conversationHistory
      );

      const assistantMessage = {
        role: 'assistant',
        content: response?.message,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again or contact support if the issue persists.',
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef?.current?.focus();
    }
  };

  const handleAnalyzeTravelRequest = async () => {
    if (!travelRequest || analyzing) return;

    setAnalyzing(true);

    const analysisMessage = {
      role: 'user',
      content: `Analyze this travel request: ${travelRequest?.destination} (${travelRequest?.travelType})`,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, analysisMessage]);

    try {
      const analysis = await travelPolicyAssistantService?.analyzeTravelRequest(travelRequest);

      const analysisContent = `**Travel Request Analysis**\n\n**Compliance Status:** ${analysis?.complianceStatus?.toUpperCase()}\n**Risk Level:** ${analysis?.riskLevel?.toUpperCase()}\n\n**Summary:**\n${analysis?.summary}\n\n**Key Concerns:**\n${analysis?.concerns?.length > 0 ? analysis?.concerns?.map((c) => `• ${c}`)?.join('\n') : '• No major concerns identified'}\n\n**Recommendations:**\n${analysis?.recommendations?.map((r) => `• ${r}`)?.join('\n')}\n\n**Required Approvals:**\n${analysis?.requiredApprovals?.map((a) => `• ${a}`)?.join('\n')}`;

      const assistantMessage = {
        role: 'assistant',
        content: analysisContent,
        timestamp: new Date(),
        isAnalysis: true,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'Failed to analyze travel request. Please try again.',
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleQuickAction = async (action) => {
    setQuickAction(action);
    setLoading(true);

    const actionMessage = {
      role: 'user',
      content: action?.label,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, actionMessage]);

    try {
      const response = await travelPolicyAssistantService?.getQuickRecommendation(action?.scenario);

      const assistantMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'Failed to get recommendation. Please try again.',
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setQuickAction(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    { id: 'emergency', label: 'Emergency Travel', icon: 'AlertTriangle', scenario: 'emergency' },
    { id: 'international', label: 'International Travel', icon: 'Globe', scenario: 'international' },
    { id: 'budget', label: 'Budget Guidelines', icon: 'DollarSign', scenario: 'budget' },
    { id: 'compliance', label: 'Compliance Rules', icon: 'Shield', scenario: 'compliance' },
  ];

  const formatTimestamp = (date) => {
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="glass-container rounded-xl border border-border/50 flex flex-col h-[600px]">
      {/* Header */}
      <div className="p-4 border-b border-border/50 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Icon name="Bot" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Travel Policy Assistant</h3>
            <p className="text-xs text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        {travelRequest && (
          <Button
            variant="outline"
            size="sm"
            iconName="Sparkles"
            onClick={handleAnalyzeTravelRequest}
            disabled={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'Analyze Request'}
          </Button>
        )}
      </div>
      {/* Quick Actions */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center space-x-2 overflow-x-auto">
          {quickActions?.map((action) => (
            <button
              key={action?.id}
              onClick={() => handleQuickAction(action)}
              disabled={loading || quickAction === action?.id}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-muted/30 hover:bg-muted/50 border border-border/30 transition-all duration-200 whitespace-nowrap disabled:opacity-50"
            >
              <Icon name={action?.icon} size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium text-foreground">{action?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message, index) => (
          <div
            key={index}
            className={`flex items-start space-x-3 ${
              message?.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
            }`}
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                message?.role === 'user' ?'bg-primary/10 border border-primary/20' :'bg-secondary/10 border border-secondary/20'
              }`}
            >
              <Icon
                name={message?.role === 'user' ? 'User' : 'Bot'}
                size={16}
                className={message?.role === 'user' ? 'text-primary' : 'text-secondary'}
              />
            </div>
            <div
              className={`flex-1 ${
                message?.role === 'user' ? 'flex flex-col items-end' : ''
              }`}
            >
              <div
                className={`rounded-lg p-3 max-w-[85%] ${
                  message?.role === 'user' ?'bg-primary text-primary-foreground ml-auto'
                    : message?.isError
                    ? 'bg-error/10 border border-error/20 text-foreground'
                    : message?.isAnalysis
                    ? 'bg-secondary/10 border border-secondary/20 text-foreground'
                    : 'bg-muted/30 border border-border/30 text-foreground'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
              </div>
              <span className="text-xs text-muted-foreground mt-1">
                {formatTimestamp(message?.timestamp)}
              </span>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center">
              <Icon name="Bot" size={16} className="text-secondary" />
            </div>
            <div className="bg-muted/30 border border-border/30 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="Loader2" size={16} className="text-muted-foreground animate-spin" />
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Input */}
      <div className="p-4 border-t border-border/50">
        <div className="flex items-end space-x-2">
          <div className="flex-1 bg-muted/20 border border-border/30 rounded-lg p-3">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about travel policies, compliance, or request analysis..."
              className="w-full bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground resize-none"
              rows={2}
              disabled={loading}
            />
          </div>
          <Button
            variant="default"
            size="icon"
            onClick={handleSendMessage}
            disabled={!inputMessage?.trim() || loading}
          >
            <Icon name="Send" size={18} />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default TravelPolicyAssistant;
